// /src/pages/ViewPolicebookPage.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";

// UI columns (table). Export uses a separate list to exclude location.
const COLUMNS = [
  { key: "location", label: "Location" },
  { key: "vin_length", label: "VIN Length" },
  { key: "INTERNAL No", label: "INTERNAL No" }, // formatted in export
  { key: "YEAR", label: "YEAR" },
  { key: "MAKE", label: "MAKE" },
  { key: "VIN", label: "VIN" },
  { key: "TITLE No", label: "TITLE No" },
  { key: "Yellow", label: "Yellow" },

  // FIX: Show stock number using a normalized key that we control.
  // We populate row.stock_number from the payload's "Stock Number".
  { key: "stock_number", label: "STOCK No" },

  { key: "COLOR", label: "COLOR" },
  { key: "DATE", label: "DATE" },
  { key: "FIRST NAME", label: "FIRST NAME" },
  { key: "MIDDLE NAME", label: "MIDDLE NAME" },
  { key: "LAST NAME", label: "LAST NAME" },
  { key: "ADDRESS", label: "ADDRESS" },
  { key: "CITY", label: "CITY" },
  { key: "STATE", label: "STATE" },
  { key: "ZIP", label: "ZIP" },
  { key: "MODEL", label: "MODEL" },
  { key: "BODY STYLE", label: "BODY STYLE" },
  { key: "CLUNKER", label: "CLUNKER" },
  { key: "AMT PAID", label: "AMT PAID" },
  { key: "DLR NO 2", label: "DLR NO 2" },
  { key: "DATE3", label: "DATE3" },
  { key: "SOLD TO", label: "SOLD TO" },
  { key: "SOLD TO ADDRESS", label: "SOLD TO ADDRESS" },
  { key: "MI DEPT OF STATE #", label: "MI DEPT OF STATE #" },
  { key: "Remarks", label: "Remarks" },
  { key: "NAME OF DEALER", label: "NAME OF DEALER" },
  { key: "DEALER LICENSE NUMBER", label: "DEALER LICENSE NUMBER" },
  { key: "VOID SD", label: "VOID SD" },
];

const API_BASE = "/api";

export default function ViewPolicebookPage() {
  const { token } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  useEffect(() => {
    let isCancelled = false;

    async function loadData() {
      setBusy(true);
      setErr("");
      try {
        const res = await fetch(`${API_BASE}/police-book`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `HTTP ${res.status}`);
        }

        const data = await res.json();
        if (isCancelled) return;

        const arr = Array.isArray(data) ? data : data.rows || [];

        // FIX: Normalize stock number from payload key "Stock Number".
        // IMPORTANT: We intentionally do NOT use "STOCK No" because it is often null.
        const normalized = arr.map((r) => {
          const stock =
            r.stock_number ??
            r["Stock Number"] ?? // <-- confirmed good key in your payload
            r.stockNumber ??
            r.stock_no ??
            r["stock number"];

          return {
            ...r,
            stock_number: stock,
          };
        });

        setRows(normalized);
      } catch (e) {
        if (!isCancelled) setErr(e?.message || "Failed to load Police Book data.");
      } finally {
        if (!isCancelled) setBusy(false);
      }
    }

    loadData();
    return () => {
      isCancelled = true;
    };
  }, [token]);

  const locations = useMemo(() => {
    const set = new Set();
    for (const r of rows) {
      if (r.location) set.add(r.location);
    }
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;

    if (locationFilter) {
      result = result.filter((r) => r.location === locationFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter((row) =>
        COLUMNS.some((col) => {
          const value = row[col.key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(q);
        })
      );
    }

    return result;
  }, [rows, locationFilter, search]);

  const handleDownload = () => {
    if (!filteredRows.length) return;

    // FIX: Exclude location from export
    const EXPORT_COLUMNS = COLUMNS.filter((c) => c.key !== "location");

    const INTERNAL_KEY = "INTERNAL No";

    const formatInternalForExcel = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).trim();
      if (!s) return "";

      const m1 = s.match(/^(\d{1,2})\D+(\d{1,2})\D+(\d+)$/);
      if (m1) {
        const mm = m1[1].padStart(2, "0");
        const dd = m1[2].padStart(2, "0");
        const seq = m1[3];
        return `${mm}${dd}-${seq}`;
      }

      const m2 = s.match(/^(\d{3,4})\D+(\d+)$/);
      if (m2) {
        const mmdd = m2[1].padStart(4, "0");
        const seq = m2[2];
        return `${mmdd}-${seq}`;
      }

      return s;
    };

    // Force Excel to treat as TEXT (prevents date coercion)
    const excelText = (s) => `="${String(s).replace(/"/g, '""')}"`;

    const headerLine = EXPORT_COLUMNS.map((c) =>
      `"${c.label.replace(/"/g, '""')}"`
    ).join(",");

    const lines = filteredRows.map((row) =>
      EXPORT_COLUMNS.map((col) => {
        const value = row[col.key];
        if (value === null || value === undefined) return '""';

        if (col.key === INTERNAL_KEY) {
          const formatted = formatInternalForExcel(value);
          return excelText(formatted);
        }

        const str = String(value);
        return `"${str.replace(/"/g, '""')}"`;
      }).join(",")
    );

    const csv = [headerLine, ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `police_book_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <Link to="/titleagent" className="btn btn-outline-secondary btn-sm">
                  ← Back
                </Link>

                <div className="fw-bold">Police Book</div>

                <Form.Select
                  size="sm"
                  style={{ minWidth: 160 }}
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </Form.Select>

                <Form.Control
                  size="sm"
                  style={{ minWidth: 220 }}
                  type="text"
                  placeholder="Search all fields..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <Button
                  size="sm"
                  variant="success"
                  onClick={handleDownload}
                  disabled={!filteredRows.length}
                >
                  Download Excel (CSV)
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {err && (
                <div className="p-3">
                  <Alert variant="danger" className="mb-0">
                    {err}
                  </Alert>
                </div>
              )}

              {busy ? (
                <div className="p-4 d-flex justify-content-center">
                  <Spinner animation="border" />
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="p-3 text-muted">No records found.</div>
              ) : (
                <div style={{ maxHeight: "75vh", overflow: "auto" }}>
                  <table className="table table-sm table-striped table-hover mb-0">
                    <thead className="table-light position-sticky top-0">
                      <tr>
                        {COLUMNS.map((col) => (
                          <th key={col.key} className="text-nowrap">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row, idx) => (
                        <tr key={row.title_id ?? idx}>
                          {COLUMNS.map((col) => {
                            const value = row[col.key];
                            return (
                              <td key={col.key} className="text-nowrap align-middle">
                                {value === null || value === undefined ? "" : String(value)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

